import {PropTypes} from 'prop-types';
import {
    createUserWithEmailAndPassword, GoogleAuthProvider, onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup,
} from 'firebase/auth';
import {getDoc, doc, setDoc} from 'firebase/firestore';
import {httpsCallable} from 'firebase/functions';
import React, {useState, useEffect, useContext, useRef, createContext} from 'react';
import {auth, db, functions} from '../firebase';
import {healUserName} from '../profileName';
import {loadIngredientAliases} from '../../views/spenza/utils/post';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({children}) {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const userInfo = useRef();
    const [storedUser, setStoredUser] = useState(null);

    async function loginWithEmailAndPassword(email, password) {
        const result = await signInWithEmailAndPassword(auth, email, password);
        return result;
    }

    async function signupWithEmailAndPassword(email, password) {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        return result;
    }

    async function createFirestoreUserInstance(user) {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
            const name = user.email.split('@')[0];
            const userImg = user.photoURL ? user.photoURL : 'https://firebasestorage.googleapis.com/v0/b/spenza-recipe-app.appspot.com/o/placeholders%2Ficon.png?alt=media&token=5fb6307c-3844-4486-b273-7a53d19b66cc';
            // Email is NOT stored in the public profile doc (any signed-in user
            // can read it). It lives in Firebase Auth + the admin-only requests.
            const userData = { uid: user.uid, name, dpUrl: userImg };
            await setDoc(userRef, userData);
            // Also write to publicUsers so the mobile Users tab can discover them
            await setDoc(doc(db, 'publicUsers', user.uid), { uid: user.uid, name, dpUrl: userImg });
        }
    }

    async function loginWithGoogle(e) {
        e.preventDefault();
        const result = await signInWithPopup(auth, new GoogleAuthProvider());
        return result;
    }

    async function refreshStoredUser() {
        if (auth.currentUser) {
            const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
            setStoredUser(snap.data());
        }
    }

    function logout() {
        auth.signOut().then((r) => {
            console.log(r);
            window.location.reload();
        }).catch((e) => {
            console.log(e);
        });
        // reload page
    }

    // Delete the signed-in account via the deleteMyAccount Cloud Function —
    // one atomic, server-side operation: cancels any Stripe subscription,
    // purges the whole users/{uid} Firestore tree + Storage, then deletes
    // the Auth account. Mirrors mobile's account-removal entry point.
    async function deleteAccount() {
        const u = auth.currentUser;
        if (!u) throw new Error('No signed-in user');
        await httpsCallable(functions, 'deleteMyAccount')();
    }

    useEffect(() => {

        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setLoading(true);
            setCurrentUser(user);

            if (user) {
                // Load custom ingredient aliases once, globally, so pantry-match
                // logic uses them on every view (recipes, cook-now, pantry) — not
                // just the Plan page, which was the only caller before.
                loadIngredientAliases(db);
                await getDoc(doc(db, "users", user.uid)).then((docu) => {
                    setStoredUser(docu.data());
                    // Self-heal denormalized author name (publicUsers + posts) if
                    // the account was renamed while those copies stayed stale.
                    healUserName(user.uid, docu.data() && docu.data().name);
                })
            }

            setLoading(false);
            console.log("User State Changed");
        });

        return unsubscribe;
    }, []);

    const value = {
        storedUser,
        currentUser,
        loginWithEmailAndPassword,
        signupWithEmailAndPassword,
        loginWithGoogle,
        logout,
        deleteAccount,
        userInfo,
        createFirestoreUserInstance,
        refreshStoredUser,
    };
  // Alternative way to handle loading state is to display a page instead of white screen:
  // {loading ? <div>Loading auth...</div> : children}
  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
    children: PropTypes.node,
};